import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Chip,
  Stack,
  Alert,
  Tooltip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import {
  fetchEncodingProfiles,
  createEncodingProfile,
  updateEncodingProfile,
  deleteEncodingProfile,
  setDefaultEncodingProfile,
} from '../store/slices/encodingProfilesSlice';
import PageHeader from '../components/PageHeader';

function EncodingProfiles() {
  const dispatch = useDispatch();
  const { profiles, error } = useSelector((state) => state.encodingProfiles);
  const { userInfo } = useSelector((state) => state.auth);
  const { activeCompanyId, list: myCompanies } = useSelector((state) => state.company);
  const activeCompany = myCompanies.find((c) => c.id === activeCompanyId);
  const isCompanyAdmin = activeCompany?.role === 'admin' || userInfo?.is_superadmin;
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    preset: 'medium',
    crf: 23,
    video_codec: 'libx264',
    audio_codec: 'copy',
    extra_flags: [],
    recommended_for: '',
    estimated_size_factor: '',
    is_default: false,
  });
  const [newFlag, setNewFlag] = useState('');

  useEffect(() => {
    dispatch(fetchEncodingProfiles());
  }, [dispatch]);

  const handleOpenDialog = (profile = null) => {
    if (profile) {
      setEditingProfile(profile);
      setFormData({
        name: profile.name,
        description: profile.description || '',
        preset: profile.preset,
        crf: profile.crf,
        video_codec: profile.video_codec,
        audio_codec: profile.audio_codec,
        output_resolution: profile.output_resolution || '',
        extra_flags: profile.extra_flags || [],
        recommended_for: profile.recommended_for || '',
        estimated_size_factor: profile.estimated_size_factor || '',
        is_default: profile.is_default,
      });
    } else {
      setEditingProfile(null);
      setFormData({
        name: '',
        description: '',
        preset: 'medium',
        crf: 23,
        video_codec: 'libx264',
        audio_codec: 'copy',
        output_resolution: '',
        extra_flags: [],
        recommended_for: '',
        estimated_size_factor: '',
        is_default: false,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProfile(null);
    setNewFlag('');
  };

  const handleSubmit = async () => {
    try {
      if (editingProfile) {
        await dispatch(updateEncodingProfile({
          id: editingProfile.id,
          ...formData,
        })).unwrap();
      } else {
        await dispatch(createEncodingProfile(formData)).unwrap();
      }
      handleCloseDialog();
    } catch (_err) {
      /* swallow — surface via redux */
    }
  };

  const handleDelete = async () => {
    if (profileToDelete) {
      try {
        await dispatch(deleteEncodingProfile(profileToDelete.id)).unwrap();
        setDeleteConfirmOpen(false);
        setProfileToDelete(null);
      } catch (_err) {
        // Error is handled by Redux
      }
    }
  };

  const handleSetDefault = async (profileId) => {
    try {
      await dispatch(setDefaultEncodingProfile(profileId)).unwrap();
    } catch (_err) {
      /* swallow — surface via redux */
    }
  };

  const handleDuplicate = (profile) => {
    setEditingProfile(null);
    const { id: _id, is_system: _is_system, ...rest } = profile;
    setFormData({
      ...rest,
      name: `${profile.name} (Copy)`,
      is_default: false,
    });
    setDialogOpen(true);
  };

  const handleAddFlag = () => {
    if (newFlag.trim()) {
      setFormData({
        ...formData,
        extra_flags: [...formData.extra_flags, newFlag.trim()],
      });
      setNewFlag('');
    }
  };

  const handleRemoveFlag = (index) => {
    setFormData({
      ...formData,
      extra_flags: formData.extra_flags.filter((_, i) => i !== index),
    });
  };

  const presetOptions = [
    'ultrafast',
    'superfast',
    'veryfast',
    'faster',
    'fast',
    'medium',
    'slow',
    'slower',
    'veryslow',
  ];

  // Only show this page to company admins (or superadmins)
  if (!isCompanyAdmin) {
    return (
      <Box>
        <Alert severity="error">
          You don't have permission to access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Encoding Profiles"
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>Add Profile</Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Default</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Settings</TableCell>
              <TableCell>Recommended For</TableCell>
              <TableCell>Size Factor</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {profiles.map((profile) => (
              <TableRow key={profile.id}>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleSetDefault(profile.id)}
                  >
                    {profile.is_default ? <StarIcon color="primary" /> : <StarBorderIcon />}
                  </IconButton>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" fontWeight={profile.is_default ? 'bold' : 'normal'}>
                      {profile.name}
                    </Typography>
                    {profile.is_system && (
                      <Chip label="System" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell>{profile.description}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <Chip label={profile.preset} size="small" />
                    <Chip label={`CRF: ${profile.crf}`} size="small" />
                    <Chip label={profile.video_codec} size="small" variant="outlined" />
                  </Stack>
                </TableCell>
                <TableCell>{profile.recommended_for}</TableCell>
                <TableCell>{profile.estimated_size_factor}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Duplicate">
                    <IconButton size="small" onClick={() => handleDuplicate(profile)}>
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={profile.is_system ? 'System profiles cannot be edited' : 'Edit'}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(profile)}
                        disabled={profile.is_system}
                      >
                        <EditIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={profile.is_system ? 'System profiles cannot be deleted' : 'Delete'}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={profile.is_system}
                        onClick={() => {
                          setProfileToDelete(profile);
                          setDeleteConfirmOpen(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingProfile ? 'Edit Encoding Profile' : 'Create Encoding Profile'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Profile Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="video-codec-label" htmlFor="video-codec-select">Video Codec</InputLabel>
                <Select
                  id="video-codec-select"
                  labelId="video-codec-label"
                  value={formData.video_codec}
                  label="Video Codec"
                  onChange={(e) => setFormData({ ...formData, video_codec: e.target.value })}
                >
                  <MenuItem value="libx264">H.264 (libx264)</MenuItem>
                  <MenuItem value="libx265">H.265/HEVC (libx265)</MenuItem>
                  <MenuItem value="libvpx-vp9">VP9 (libvpx-vp9)</MenuItem>
                  <MenuItem value="libaom-av1">AV1 (libaom-av1)</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl fullWidth>
                <InputLabel id="encoding-speed-label" htmlFor="encoding-speed-select">Encoding Speed</InputLabel>
                <Select
                  id="encoding-speed-select"
                  labelId="encoding-speed-label"
                  value={formData.preset}
                  label="Encoding Speed"
                  onChange={(e) => setFormData({ ...formData, preset: e.target.value })}
                >
                  {presetOptions.map((preset) => (
                    <MenuItem key={preset} value={preset}>
                      {preset}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <Box>
              <Typography gutterBottom>
                Quality (CRF): {formData.crf}
              </Typography>
              <Slider
                value={formData.crf}
                onChange={(e, value) => setFormData({ ...formData, crf: value })}
                min={0}
                max={51}
                marks={[
                  { value: 0, label: 'Lossless' },
                  { value: 18, label: 'High' },
                  { value: 23, label: 'Medium' },
                  { value: 28, label: 'Low' },
                  { value: 51, label: 'Worst' },
                ]}
              />
            </Box>
            
            <FormControl fullWidth>
              <InputLabel id="audio-codec-label" htmlFor="audio-codec-select">Audio Codec</InputLabel>
              <Select
                id="audio-codec-select"
                name="audio_codec"
                labelId="audio-codec-label"
                value={formData.audio_codec}
                label="Audio Codec"
                onChange={(e) => setFormData({ ...formData, audio_codec: e.target.value })}
              >
                <MenuItem value="copy">Copy (No Re-encoding)</MenuItem>
                <MenuItem value="aac">AAC</MenuItem>
                <MenuItem value="libmp3lame">MP3</MenuItem>
                <MenuItem value="libopus">Opus</MenuItem>
                <MenuItem value="ac3">AC3</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="output-resolution-label" htmlFor="output-resolution-select">Output Resolution</InputLabel>
              <Select
                id="output-resolution-select"
                labelId="output-resolution-label"
                value={formData.output_resolution}
                label="Output Resolution"
                onChange={(e) => setFormData({ ...formData, output_resolution: e.target.value })}
              >
                <MenuItem value="">Original (no scaling)</MenuItem>
                <MenuItem value="1920x1080">1920x1080 (1080p)</MenuItem>
                <MenuItem value="1280x720">1280x720 (720p)</MenuItem>
                <MenuItem value="3840x2160">3840x2160 (4K)</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ mt: 2 }}>
              <Typography gutterBottom>Additional FFmpeg Flags</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="e.g., -movflags +faststart"
                  value={newFlag}
                  onChange={(e) => setNewFlag(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddFlag();
                    }
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAddFlag}
                >
                  Add
                </Button>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {formData.extra_flags.map((flag, index) => (
                  <Chip
                    key={index}
                    label={flag}
                    onDelete={() => handleRemoveFlag(index)}
                    size="small"
                  />
                ))}
              </Stack>
            </Box>
            
            <TextField
              label="Recommended For"
              value={formData.recommended_for}
              onChange={(e) => setFormData({ ...formData, recommended_for: e.target.value })}
              fullWidth
              placeholder="e.g., Web streaming, mobile devices"
            />
            
            <TextField
              label="Estimated Size Factor"
              value={formData.estimated_size_factor}
              onChange={(e) => setFormData({ ...formData, estimated_size_factor: e.target.value })}
              fullWidth
              placeholder="e.g., 0.5x original"
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_default}
                  onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                />
              }
              label="Set as Default"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingProfile ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the encoding profile "{profileToDelete?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EncodingProfiles;